import { useCallback, useRef } from 'react'

/**
 * Returns a function with a **stable identity** (never recreated) that always
 * delegates to the latest version of `fn`.
 *
 * Use this instead of `useCallback` when you need a callback whose reference
 * never changes but that still reads the latest props/state when invoked.
 *
 * ```ts
 * const handleSave = useStableCallback(async () => {
 *   await save(draft)        // reads current `draft` at call-time
 * })
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useStableCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn)
  ref.current = fn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(((...args: unknown[]) => ref.current(...args)) as T, [])
}
